import React, { useState, useEffect, useMemo, useRef } from "react";
import { Layout, Select, Form, Input, TimePicker, Button, Modal, Checkbox } from "../../components/ui";
import {
  useGetChildAttendanceByMonth,
  useGetChildById,
  useMarkChildAttendance,
  useUpdateChildAttendance,
  useDeleteChildAttendance,
} from "../../query/childQuery";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import swal from "../../utils/swal";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Plus,
} from "lucide-react";
import { UPLOADS_URL } from "../../config/constants";
import "../teachers/teacherDetails.css";
import "../teachers/teacherAttendance.css";
import { useGetTeacherRules } from "../../query/settingsQuery";
import { DEFAULT_SCHOOL_TIME_ZONE, schoolClockParts, schoolDayKey, schoolToday } from "../../utils/schoolTime";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function dayWord(record) {
  if (record?.status === "PRESENT" && record.late) return "Late";
  if (record?.status === "ABSENT") return "Absent";
  if (record?.status === "LEAVE") return "Leave";
  if (record?.status === "HOLIDAY") return "Holiday";
  return "Present";
}

function initials(firstName = "", lastName = "") {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "S";
}

function cellClass(day, records, selectedDate, currentMonth, todayKey) {
  const inMonth = day.month() + 1 === Number(currentMonth);
  const selected = day.isSame(selectedDate, "day");
  const weekend = day.day() === 0 || day.day() === 6;
  const status = records[0]?.status;
  const classes = ["ta-cell"];
  if (!inMonth) classes.push("is-muted");
  if (weekend) classes.push("is-weekend");
  if (todayKey && day.format("YYYY-MM-DD") === todayKey) classes.push("is-today");
  if (selected) classes.push("is-selected");
  if (status === "ABSENT") classes.push("is-absent");
  else if (status === "HOLIDAY") classes.push("is-holiday");
  else if (status === "LEAVE") classes.push("is-leave");
  else if (status === "PRESENT") classes.push("is-present");
  if (records.length > 1) classes.push("is-multi");
  return classes.join(" ");
}

function ChildAttendance() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { Option } = Select;

  const [month, setMonth] = useState((dayjs().month() + 1).toString());
  const [year, setYear] = useState(dayjs().year().toString());
  const [allAttendance, setAllAttendance] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState();
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [form] = Form.useForm();
  const status = Form.useWatch("status", form);

  const { data: child } = useGetChildById(id);
  const { data: rules } = useGetTeacherRules();
  const timeZone = rules?.timeZone || DEFAULT_SCHOOL_TIME_ZONE;
  const alignedZone = useRef("");
  const { isLoading, data: attendance, refetch } = useGetChildAttendanceByMonth({ id, month, year });
  const { mutate: markAttendance, isPending: isMutating } = useMarkChildAttendance();
  const { mutate: updateAttendance, isPending: isMutating2 } = useUpdateChildAttendance();
  const { mutate: deleteAttendance, isPending: isMutating3 } = useDeleteChildAttendance();

  const fullName = `${child?.firstName || ""} ${child?.lastName || ""}`.trim() || "Student";
  const todayKey = schoolToday(timeZone).key;

  useEffect(() => {
    setAllAttendance(Array.isArray(attendance) ? attendance : []);
  }, [attendance]);

  useEffect(() => {
    if (!timeZone || alignedZone.current === timeZone) return;
    alignedZone.current = timeZone;
    const today = schoolToday(timeZone);
    setMonth(String(today.month));
    setYear(String(today.year));
    setSelectedDate(dayjs(today.key).toDate());
  }, [timeZone]);

  const events = useMemo(
    () =>
      (allAttendance || []).map((item) => ({
        id: item._id,
        title: item.status,
        label: child?.firstName || item.status,
        start: new Date(item.checkIn),
        end: item.checkOut ? new Date(item.checkOut) : new Date(item.checkIn),
        status: item.status,
        checkIn: item.checkIn,
        checkOut: item.checkOut,
        name: item.name,
        leaveReason: item.leaveReason,
        late: Boolean(item.late),
      })),
    [allAttendance, child?.firstName]
  );

  const selectedRecords = events.filter(
    (item) => schoolDayKey(item.checkIn, timeZone) === dayjs(selectedDate).format("YYYY-MM-DD")
  );

  const monthlyStats = useMemo(() => {
    const counts = { PRESENT: 0, ABSENT: 0, LEAVE: 0, HOLIDAY: 0 };
    events.forEach((item) => {
      if (counts[item.status] !== undefined) counts[item.status] += 1;
    });
    const tracked = counts.PRESENT + counts.ABSENT + counts.LEAVE;
    const rate = tracked ? Math.round((counts.PRESENT / tracked) * 100) : 0;
    const total = tracked + counts.HOLIDAY;
    return { ...counts, tracked, rate, total };
  }, [events]);

  const calendarDays = useMemo(() => {
    const start = dayjs(`${year}-${String(month).padStart(2, "0")}-01`);
    const mondayIndex = (start.day() + 6) % 7;
    const gridStart = start.subtract(mondayIndex, "day");
    return Array.from({ length: 42 }, (_, index) => gridStart.add(index, "day"));
  }, [month, year]);

  const recordsOn = (day) =>
    events.filter((item) => schoolDayKey(item.checkIn, timeZone) === day.format("YYYY-MM-DD"));

  const goToMonth = (offset) => {
    const next = dayjs(`${year}-${String(month).padStart(2, "0")}-01`).add(offset, "month");
    setMonth(String(next.month() + 1));
    setYear(String(next.year()));
    setSelectedDate(next.toDate());
  };

  const goToday = () => {
    const today = schoolToday(timeZone);
    setMonth(String(today.month));
    setYear(String(today.year));
    setSelectedDate(dayjs(today.key).toDate());
  };

  const handleSelectDay = (day) => {
    setSelectedDate(day.toDate());
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setIsEditing(true);
    setSelectedDate(event.checkIn ? new Date(event.checkIn) : event.start);
    const checkInParts = event.checkIn ? schoolClockParts(event.checkIn, timeZone) : null;
    const checkOutParts = event.checkOut ? schoolClockParts(event.checkOut, timeZone) : null;
    form.setFieldsValue({
      id: event.id,
      status: event.status,
      date: dayjs(schoolDayKey(event.checkIn, timeZone)).toDate(),
      leaveReason: event.leaveReason,
      late: Boolean(event.late),
      checkIn: checkInParts ? dayjs().hour(checkInParts.hour).minute(checkInParts.minute).second(0) : null,
      checkOut: checkOutParts ? dayjs().hour(checkOutParts.hour).minute(checkOutParts.minute).second(0) : null,
      name: event.name,
    });
    setModalOpen(true);
  };

  const openAddModal = (date = selectedDate) => {
    form.resetFields();
    form.setFieldsValue({ status: "PRESENT", date, late: false });
    setSelectedDate(date);
    setIsEditing(false);
    setSelectedEvent(undefined);
    setModalOpen(true);
  };

  const handleSave = () => {
    const values = form.getFieldsValue();
    if (!values.status) {
      swal("Error", "Status is required", "error");
      return;
    }
    if (values.status === "PRESENT" && !values.checkIn) {
      swal("Error", "Check-in time is required", "error");
      return;
    }
    if (values.status === "LEAVE" && !values.leaveReason) {
      swal("Error", "Leave reason is required", "error");
      return;
    }

    const isSpecialStatus = ["ABSENT", "LEAVE", "HOLIDAY"].includes(values.status);
    const schoolDate = dayjs(values.date || selectedDate).format("YYYY-MM-DD");
    const payload = {
      title: values.status,
      status: values.status,
      schoolDate,
      leaveReason: values.leaveReason || null,
      name: values.name || null,
      checkInClock: isSpecialStatus ? "00:00" : values.checkIn?.format?.("HH:mm"),
      checkOutClock: isSpecialStatus ? undefined : values.checkOut?.format?.("HH:mm"),
      late: !isSpecialStatus && Boolean(values.late),
    };

    const onDone = (message) => ({
      onSuccess: () => {
        refetch();
        swal("Success", message, "success");
        setModalOpen(false);
      },
      onError: (error) => {
        swal("Error", error?.message, "error");
      },
    });

    if (isEditing && selectedEvent) {
      updateAttendance({ attendanceId: selectedEvent.id, data: payload }, onDone("Attendance updated successfully"));
    } else {
      markAttendance({ childId: id, data: payload }, onDone("Attendance added successfully"));
    }
  };

  const handleDelete = () => {
    swal({
      title: "Are you sure?",
      text: "Do you want to delete the record?",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then(async function (isConfirm) {
      if (isConfirm) {
        deleteAttendance(selectedEvent.id, {
          onSuccess: () => {
            refetch();
            swal("Success", "Attendance deleted successfully", "success");
            setModalOpen(false);
          },
          onError: (error) => {
            swal("Error", error?.message, "error");
          },
        });
      }
    });
  };

  return (
    <Layout className="configuration">
      <div className="ta-page is-student">
        <div className="ta-layout">
          <div className="ta-card">
            {isLoading ? <span className="ta-loading">Updating calendar…</span> : null}
            <div className="ta-cal-bar">
              <div className="ta-cal-lead">
                <button
                  type="button"
                  className="ta-back"
                  aria-label="Back to student details"
                  onClick={() => navigate(`/children/${id}`)}
                >
                  <ArrowLeft size={16} />
                </button>
                <div className="ta-identity">
                  {child?.image ? (
                    <img className="ta-avatar" src={`${UPLOADS_URL}/${child.image}`} alt={fullName} />
                  ) : (
                    <span className="ta-avatar">{initials(child?.firstName, child?.lastName)}</span>
                  )}
                  <div>
                    <h3>{fullName}</h3>
                    <p>Student attendance</p>
                  </div>
                </div>
              </div>
              <div className="ta-cal-tools">
              <div className="ta-month-nav">
                <button type="button" aria-label="Previous month" onClick={() => goToMonth(-1)}>
                  <ChevronLeft size={16} />
                </button>
                <h3>{dayjs(`${year}-${String(month).padStart(2, "0")}-01`).format("MMMM YYYY")}</h3>
                <button type="button" aria-label="Next month" onClick={() => goToMonth(1)}>
                  <ChevronRight size={16} />
                </button>
                <button type="button" className="ta-today" onClick={goToday}>
                  Today
                </button>
              </div>
              <button type="button" className="ta-new" onClick={() => openAddModal(selectedDate)}>
                <Plus size={14} />
                New Attendance
              </button>
              </div>
            </div>

            <div className="ta-weekdays">
              {WEEKDAYS.map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>

            <div className="ta-month">
              {calendarDays.map((day) => {
                const records = recordsOn(day);
                const key = day.format("YYYY-MM-DD");
                return (
                  <button
                    type="button"
                    key={key}
                    className={cellClass(day, records, selectedDate, month, todayKey)}
                    onClick={() => handleSelectDay(day)}
                  >
                    <span className="ta-cell-num">{day.date()}</span>

                    {records.length === 1 ? (
                      <span
                        className={`ta-cell-chip is-${records[0].status === "PRESENT" && records[0].late ? "late" : records[0].status.toLowerCase()}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSelectEvent(records[0]);
                        }}
                      >
                        {dayWord(records[0])}
                      </span>
                    ) : null}

                    {records.length > 1 ? (
                      <div className="ta-cell-dots">
                        {records.slice(0, 2).map((item) => (
                          <span
                            key={item.id}
                            className={`ta-cell-chip is-${item.status === "PRESENT" && item.late ? "late" : item.status.toLowerCase()}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelectEvent(item);
                            }}
                          >
                            {dayWord(item)}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <aside className="ta-side">
            <div className="ta-side-head">
              <h3>This month</h3>
              <strong>{monthlyStats.rate}%</strong>
            </div>
            <div className="ta-stat-grid">
              {[
                { key: "PRESENT", label: "Present", tone: "green" },
                { key: "ABSENT", label: "Absent", tone: "red" },
                { key: "LEAVE", label: "Leave", tone: "yellow" },
                { key: "HOLIDAY", label: "Holiday", tone: "navy" },
              ].map((row) => (
                <article key={row.key} className={`ta-stat is-${row.tone}`}>
                  <b>{monthlyStats[row.key]}</b>
                  <span>{row.label}</span>
                </article>
              ))}
            </div>
            <div className="ta-day">
              <h4>{dayjs(selectedDate).format("ddd, D MMM")}</h4>
              {selectedRecords.length ? (
                selectedRecords.map((item) => (
                  <button key={item.id} type="button" className="ta-day-row" onClick={() => handleSelectEvent(item)}>
                    <i className={`is-${item.status === "PRESENT" && item.late ? "late" : String(item.status).toLowerCase()}`} />
                    <span>{dayWord(item)}</span>
                  </button>
                ))
              ) : (
                <p>No attendance on this day.</p>
              )}
            </div>
          </aside>
        </div>
      </div>

      <Modal
        className="ta-modal"
        title={isEditing ? "Edit Attendance" : "Add Attendance"}
        open={isModalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          isEditing ? (
            <Button key="delete" danger onClick={handleDelete} loading={isMutating3}>
              Delete
            </Button>
          ) : null,
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSave} loading={isMutating || isMutating2}>
            Save
          </Button>,
        ].filter(Boolean)}
      >
        <Form initialValues={{ status: "PRESENT" }} form={form} layout="vertical">
          {selectedDate ? (
            <p className="ta-modal-date">
              {dayjs(selectedDate).format("dddd, D MMMM YYYY")} · {timeZone}
            </p>
          ) : null}

          <Form.Item name="status" label="Status" rules={[{ required: true, message: "Status is required" }]}>
            <Select placeholder="Select status" defaultValue="PRESENT">
              <Option value="PRESENT">Present</Option>
              <Option value="ABSENT">Absent</Option>
              <Option value="LEAVE">Leave</Option>
            </Select>
          </Form.Item>

          {status === "PRESENT" ? (
            <>
              <Form.Item
                name="checkIn"
                label="Check-in time"
                rules={[{ required: true, message: "Check-in is required" }]}
              >
                <TimePicker style={{ width: "100%" }} size="large" format="hh:mm A" />
              </Form.Item>
              <Form.Item name="checkOut" label="Check-out time">
                <TimePicker style={{ width: "100%" }} size="large" format="hh:mm A" />
              </Form.Item>
              <Form.Item name="late" valuePropName="checked">
                <Checkbox>Late</Checkbox>
              </Form.Item>
            </>
          ) : null}

          {status === "LEAVE" ? (
            <Form.Item
              name="leaveReason"
              label="Leave reason"
              rules={[{ required: true, message: "Leave reason is required" }]}
            >
              <Input placeholder="Enter reason" />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    </Layout>
  );
}

export default ChildAttendance;
